import React, { useEffect, useState } from 'react';   
import styles from './DefineStructure.module.css';    
import layout from '../../../../Modules/layout.module.css';    
import effects from '../../../../Modules/effects.module.css';    
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';    
import ActionBar from '../../../../Components/Utils/Actions/ActionBar/ActionBar';    
import CreationBar from '../../../../Components/Utils/Actions/CreationBar/CreationBar';    
import Button from '../../../../Components/Utils/Button/Button';    
import TitledField from '../../../../Components/Utils/TitledField/TitledField';    
import Checkbox from '../../../../Components/Utils/Checkbox/Checkbox';    
import { IQuestionnaireSection } from '../../../../Types/Products/IQuestionnaireSection';    
import { ICriteriaItem } from '../../../../Types/Products/ICriteriaItemData';  
import { CreateCriteriaByQuestionnaireTemplateIdAndSectionIdAndTitle, CreateSectionByQuestionnaireTemplateId, DeleteSectionsAndCriteriaByTemplateId, GetSectionsByTemplateId, UpdateCriteriaTitleById, UpdateSectionTitleById, ReorderSectionInTemplate, ReorderCriteriaInSection, CompleteQuestionnaireTemplateById, CompleteTemplateWithoutMandate } from '../../../../API/TemplateBank/TemplateBank';  
import { getAiPowered, getClient, getClientHasThirdPartySubsets, getClientRolePermission } from '../../../../Redux/Store/Slices/client';
import { useAppSelector } from '../../../../Redux/Store/Store';
import DropDownList from '../../../../Components/Utils/DropDownList/DropDownList';
import { GetAvailableGradeProfiles } from '../../../../API/Evaluate/Evaluate';
import { IDropDownListItem } from '../../../../Types/DropDownList/IDropDownListItem';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import { useWorkflowContext } from '../../../../Hooks/useWorkflowContext';
import { motion, AnimatePresence } from 'framer-motion';
import Tooltip from '../../../../Components/Utils/Tooltip/Tooltip';
import ConfirmAction from '../../../../Components/Utils/ConfirmAction/ConfirmAction';
import { getProductTypeFromPath } from '../../../../Helpers/Paths';
import { ProductType } from '../../../../Types/ProductType';
import { beginPendingSave, useUnsavedChangesWarning } from '../../../../Hooks/useUnsavedChangesWarning';
  
type DefineStructureProps = object;      

let updateTimer: NodeJS.Timeout | null = null; // Declare the timer variable
let endPendingDebouncedSave: (() => void) | null = null;
const debounceDelay = 500;

function scheduleDebouncedStructureSave(run: () => Promise<void>) {
  if (updateTimer) {
    clearTimeout(updateTimer);
  } else {
    endPendingDebouncedSave = beginPendingSave();
  }

  updateTimer = setTimeout(async () => {
    updateTimer = null;
    try {
      await run();
    } catch (error) {
      console.error(error);
    } finally {
      endPendingDebouncedSave?.();
      endPendingDebouncedSave = null;
    }
  }, debounceDelay);
}

const normalisePreviewColour = (rawColour?: string): string => {
  if (!rawColour) return '#EEF2F7';
  const value = rawColour.trim();
  const key = value.toLowerCase();

  const colourMap: Record<string, string> = {
    red: '#E25A74',
    amber: '#EAB33C',
    orange: '#F59E0B',
    yellow: '#FACC15',
    green: '#52BFA2',
    darkgreen: '#2E7D32',
    blue: '#4F7CAC',
    purple: '#7E57C2',
    pink: '#EC4899',
    grey: '#9CA3AF',
    gray: '#9CA3AF',
    white: '#FFFFFF',
    clear: 'transparent',
    black: '#000000',
  };

  return colourMap[key] ?? value;
};
  
const DefineStructure = ({}: DefineStructureProps) => {      
  const { templateId, templateTitle } = useParams();
  const location = useLocation();
  const { getWorkflowPath, isAdminContext } = useWorkflowContext();
  const isDevelop = getProductTypeFromPath(location.pathname) === ProductType.Develop;
        
  /*const temp: IQuestionnaireSection[] = [        
    {          
      id: '1',          
      title: 'Behaviour and attitudes',          
      description: '',          
      active: false,          
      criteria: [            
        {              
          id: '11',              
          title: 'BA1 Behaviour and attitudes',              
          description: '',              
          active: false,              
          questions: [],              
          grade: 'Red',              
          tags: [],              
          fields: []            
        },            
        {              
          id: '12',              
          title: 'BA2 Attendance',              
          description: '',              
          active: false,              
          questions: [],              
          grade: 'Red',              
          tags: [],              
          fields: []            
        }          
      ],          
      completionStatus: 'Complete'        
    },        
    {          
      id: '2',          
      title: 'Behaviour and attitudes',          
      description: '',          
      active: false,          
      criteria: [            
        {              
          id: '21',              
          title: 'BA1 Behaviour and attitudes',              
          description: '',              
          active: false,              
          questions: [],              
          grade: 'Red',              
          tags: [],              
          fields: []            
        },            
        {              
          id: '22',              
          title: 'BA2 Attendance',              
          description: '',              
          active: false,              
          questions: [],              
          grade: 'Red',              
          tags: [],              
          fields: []            
        }          
      ],          
      completionStatus: 'Complete'        
    }      
  ];*/

  const client = useAppSelector(getClient);
    
  const [selectedSections, setSelectedSections] = useState<string[]>([]);      
  const [selectedItems, setSelectedItems] = useState<string[]>([]);      
  const [sections, setQuestionnaireSections] = useState<IQuestionnaireSection[]>([]);      
  const [newCriteriaTitles, setNewCriteriaTitles] = useState<{ [key: string]: string }>({}); // State to manage new criteria titles for each section  

  useUnsavedChangesWarning(
    Object.values(newCriteriaTitles).some((title) => title.trim().length > 0)
  );
  const [confirmNonAIVisible, setConfirmNonAIVisible] = useState(false);

  const [gradeProfiles, setGradeProfiles] = useState<IDropDownListItem[]>([]); // State to manage grade profiles
  const [selectedGradeProfile, setSelectedGradeProfile] = useState<string>(); // State to manage selected grade profile
  const [gradeProfilePreviewOptions, setGradeProfilePreviewOptions] = useState<IDropDownListItem[]>([]);

  const rolePermissions = useAppSelector(getClientRolePermission);
  const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);

  const isAiPowered = useAppSelector(getAiPowered);

  const navigate = useNavigate();  

  useEffect(() => {
    const getGradeProfiles = async () => {
      if (client && templateId) {
        const profiles = await GetAvailableGradeProfiles();
        setGradeProfiles(profiles);

        const gradeProfileId = (await axiosInstance.get(`GetGradeProfileByTemplateId/${templateId}`)).data as string;

        setSelectedGradeProfile(gradeProfileId);
      }
    };
    getGradeProfiles();
  }, []);

  useEffect(() => {    
    const getSections = async () => {        
      if(templateId && client) {
        const response = await GetSectionsByTemplateId(templateId, client.id);          
        setQuestionnaireSections(response);        
      }      
    };      
    getSections();  
  }, [templateId]);        
    
  const handleSectionSelection = (sectionId: string, selected: boolean) => {  
    setSelectedSections((prevSelectedSections) => {  
      let newSelectedSections = [...prevSelectedSections];  
      const section = sections.find((section) => section.id === sectionId);  
  
      if (section) {  
        if (selected) {  
          if (!newSelectedSections.includes(sectionId)) {  
            newSelectedSections.push(sectionId);  
          }  
          // Add all criteria in the section to selectedItems  
          setSelectedItems((prevSelectedItems) => {  
            let newSelectedItems = [...prevSelectedItems];  
            section.criteria.forEach((criterion) => {  
              if (!newSelectedItems.includes(criterion.id)) {  
                newSelectedItems.push(criterion.id);  
              }  
            });  
            return newSelectedItems;  
          });  
        } else {  
          newSelectedSections = newSelectedSections.filter((id) => id !== sectionId);  
          // Remove all criteria in the section from selectedItems  
          setSelectedItems((prevSelectedItems) => {  
            return prevSelectedItems.filter((id) => !section.criteria.some((criterion) => criterion.id === id));  
          });  
        }  
      }  
  
      return newSelectedSections;  
    });  
  };  
  
  const handleItemSelection = (itemId: string, selected: boolean) => {        
    setSelectedItems((prevSelectedItems) => {          
      let newSelectedItems = [...prevSelectedItems];            
      if (selected) {            
        if (!newSelectedItems.includes(itemId)) {              
          newSelectedItems.push(itemId);            
        }          
      } else {            
        newSelectedItems = newSelectedItems.filter((id) => id !== itemId);          
      }            
      return newSelectedItems;        
    });      
  };          
  
  const handleSectionTitleChange = async (sectionId: string, newTitle: string) => {
    const sectionToUpdate = sections.find((section) => section.id === sectionId);
  
    if (sectionToUpdate) {
      scheduleDebouncedStructureSave(async () => {
        await UpdateSectionTitleById(newTitle, sectionToUpdate.id);
      });
    }

    setQuestionnaireSections((prevState) =>
      prevState.map((section) =>
        section.id === sectionId ? { ...section, title: newTitle } : section
      )
    );
  };

  const handleCriteriaTitleChange = (criteriaId: string, newTitle: string) => {
    const sectionToUpdate = sections.find((section) =>
      section.criteria.some((criterion) => criterion.id === criteriaId)
    );

    if (sectionToUpdate) {
      const criterionToUpdate = sectionToUpdate.criteria.find((criterion) => criterion.id === criteriaId);

      setQuestionnaireSections((prevState) =>
        prevState.map((section) =>
          section.id === sectionToUpdate.id
            ? {
                ...section,
                criteria: section.criteria.map((criterion) =>
                  criterion.id === criteriaId ? { ...criterion, title: newTitle } : criterion
                ),
              }
            : section
        )
      );

      if (criterionToUpdate) {
        scheduleDebouncedStructureSave(async () => {
          if (client) {
            await UpdateCriteriaTitleById(newTitle, criterionToUpdate.id, client.id);
          }
        });
      }
    }
  };
    
  const deleteItems = async () => {  
    if (templateId && client) {  
      try {  
        await DeleteSectionsAndCriteriaByTemplateId(templateId, [...selectedSections, ...selectedItems], client.id);  
        setQuestionnaireSections((prevFields) =>  
          prevFields  
            .filter((field) => !selectedSections.includes(field.id)) // Remove entire sections that are selected  
            .map((field) => ({  
              ...field,  
              criteria: field.criteria.filter((item) => !selectedItems.includes(item.id)), // Remove selected criteria  
            }))  
        );  
        setSelectedSections([]);  
        setSelectedItems([]);  
      } catch (error) {  
        console.error('Error deleting items:', error);  
      }  
    }  
  };  
    
  const createSection = async (title: string) => {    
    if(templateId && client){        
      const newSection = await CreateSectionByQuestionnaireTemplateId(templateId, title, client.id);        
      setQuestionnaireSections([...sections, newSection]);        
    }    
  };    
    
  const createCriteria = async (sectionId: string, title: string) => {    
    if(templateId && client){        
      const newCriterion = await CreateCriteriaByQuestionnaireTemplateIdAndSectionIdAndTitle(templateId, sectionId, title, client.id);  
      setQuestionnaireSections((prevSections) =>            
        prevSections.map(section => {              
          if (section.id === sectionId) {                
            return {                  
              ...section,                  
              criteria: [...section.criteria, newCriterion]                
            };              
          }              
          return section;            
        })          
      );        
    }    
  };        
    
  const handleCriteriaInputChange = async (sectionId: string, newTitle: string) => {  
    setNewCriteriaTitles((prevTitles) => ({  
      ...prevTitles,  
      [sectionId]: newTitle  
    }));  
  };  
  
  const handleCriteriaInputBlur = (sectionId: string) => {  
    if (newCriteriaTitles[sectionId]) {  
      createCriteria(sectionId, newCriteriaTitles[sectionId]);  
      setNewCriteriaTitles((prevTitles) => ({  
        ...prevTitles,  
        [sectionId]: ''  
      }));  
    }  
  };  

  const handleGradeProfileChange = async (selectedGradeItem: IDropDownListItem) => {
      await axiosInstance.get(`UpdateGradeProfileByTemplateId/${templateId}/${selectedGradeItem.value}`)
      setSelectedGradeProfile(selectedGradeItem.value);
    };

  useEffect(() => {
    const loadGradeProfilePreview = async () => {
      if (!selectedGradeProfile) {
        setGradeProfilePreviewOptions([]);
        return;
      }
      try {
        const options = (await axiosInstance.get(`GetOptionsByGradeProfileId/${selectedGradeProfile}`)).data as IDropDownListItem[];
        setGradeProfilePreviewOptions(options ?? []);
      } catch (error) {
        console.error('Error loading grade profile preview options:', error);
        setGradeProfilePreviewOptions([]);
      }
    };

    loadGradeProfilePreview();
  }, [selectedGradeProfile]);

  const handleMoveSection = async (sectionId: string, direction: 'up' | 'down') => {
    if (!templateId || !client) return;

    try {
      const updatedSections = await ReorderSectionInTemplate(templateId, sectionId, direction, client.id);
      setQuestionnaireSections(updatedSections);
    } catch (error) {
      console.error('Error reordering section:', error);
    }
  };

  const handleMoveCriteria = async (sectionId: string, criteriaId: string, direction: 'up' | 'down') => {
    if (!templateId || !client) return;

    try {
      const updatedSections = await ReorderCriteriaInSection(templateId, sectionId, criteriaId, direction, client.id);
      setQuestionnaireSections(updatedSections);
    } catch (error) {
      console.error('Error reordering criteria:', error);
    }
  };

  return (        
    <>
   
    <div className={layout.container}>          
      
      <div className={`${layout.column1} ${styles.pageShell}`}>     

        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderEyebrow}>Template Structure</div>
          <h1 className={styles.pageTitle}>{templateTitle}</h1>    
          <p className={styles.pageSubtitle}>Define the structure of the diagnostic</p>     
        </div>    
        
        <div className={styles.actionBarSection}>
          <ActionBar enabledFeatures={['delete']} selectedCount={[...selectedItems, ...selectedSections].length} onDeleteHandler={deleteItems} />      
        </div>

        <div className={styles.sectionsSection}>
          <div className={styles.sectionGroupHeader}>
            <div>
              <p className={styles.sectionGroupTitle}>Sections and Criteria</p>
              <p className={styles.sectionGroupHint}>Build the diagnostic framework by creating sections and adding criteria inside each section.</p>
            </div>
            <span className={styles.sectionCountBadge}>{sections.length} sections</span>
          </div>

        <AnimatePresence mode="popLayout">
        {sections.map(({ id, title, description, criteria }, sectionIndex) => {              
          return (                
            <motion.div 
              key={id} 
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.2,
                layout: { duration: 0.3, ease: "easeInOut" }
              }}
              className={styles.sectionContainer}
            >
              <div className={styles.sectionField}>
                <TitledField                  
                  title={title}                  
                  text={description}                  
                  onSelectedHandler={(selected: boolean) => handleSectionSelection(id, selected)}                  
                  titleChanged={(newTitle: string) => handleSectionTitleChange(id, newTitle)}                
                >                  
                  <div className={`${layout.column} `}>                    
                    <AnimatePresence mode="popLayout">
                    {criteria.map(({ id: criterionId, title: criterionTitle, description: criterionDescription }, criteriaIndex) => {                      
                      return (                        
                        <motion.div 
                          key={criterionId} 
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ 
                            duration: 0.2,
                            layout: { duration: 0.3, ease: "easeInOut" }
                          }}
                          className={styles.criteriaContainer}
                        >
                          <div className={styles.criteriaField}>
                            <div className={styles.criteriaRow}>                          
                              <Checkbox                            
                                clickHandler={(selected: boolean) => handleItemSelection(criterionId, selected)}                            
                                value={selectedItems.includes(criterionId)}                          
                              />                          
                              <input
                                  value={criterionTitle}
                                  className={styles.criteriaTitleInput}
                                  onChange={(e) => handleCriteriaTitleChange(criterionId, e.target.value)}
                                />             
                            </div>
                          </div>
                          <div className={styles.reorderButtons}>
                            <button 
                              className={styles.reorderButton}
                              onClick={() => handleMoveCriteria(id, criterionId, 'up')}
                              disabled={criteriaIndex === 0}
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button 
                              className={styles.reorderButton}
                              onClick={() => handleMoveCriteria(id, criterionId, 'down')}
                              disabled={criteriaIndex === criteria.length - 1}
                              title="Move down"
                            >
                              ▼
                            </button>
                          </div>
                        </motion.div>                        
                      );                      
                    })}
                    </AnimatePresence>
                    <input   
                    className={styles.inputCreate}
                      placeholder='+ Add Criteria'   
                      value={newCriteriaTitles[id] || ''} // Bind input to state for the specific section  
                      onChange={(e) => handleCriteriaInputChange(id, e.target.value)} // Update state on input change for the specific section  
                      onBlur={() => handleCriteriaInputBlur(id)} // Handle blur event for the specific section  
                      onKeyDown={(e) => {  
                        if (e.key === 'Enter') {  
                          handleCriteriaInputBlur(id); // Handle enter key event  
                          e.preventDefault(); // Prevent default behavior to avoid form submission if inside a form  
                        }  
                      }}  
                    />  
                  </div>                
                </TitledField>
              </div>
              <div className={styles.reorderButtons}>
                <button 
                  className={styles.reorderButton}
                  onClick={() => handleMoveSection(id, 'up')}
                  disabled={sectionIndex === 0}
                  title="Move up"
                >
                  ▲
                </button>
                <button 
                  className={styles.reorderButton}
                  onClick={() => handleMoveSection(id, 'down')}
                  disabled={sectionIndex === sections.length - 1}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
            </motion.div>              
          );            
        })}
        </AnimatePresence>
        </div>

        <div className={styles.createSectionCard}>
          <div className={styles.settingsCardHeader}>
            <p className={styles.settingsCardTitle}>Add a New Section</p>
            <p className={styles.settingsCardHint}>Create another section to expand the diagnostic structure.</p>
          </div>
          <CreationBar              
            onCreateHandler={createSection}              
            placeholder={'Enter title of new section...'}              
            noTextMessage={'*'}            
          />
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.settingsCardHeader}>
            <p className={styles.settingsCardTitle}>Grade Profile</p>
            <p className={styles.settingsCardHint}>Choose the grading framework used for this template.</p>
          </div>
          <DropDownList onChange={handleGradeProfileChange} value={selectedGradeProfile ?? ""} placeholder={'Select a grade profile'} data={gradeProfiles}/>
          <div className={styles.gradePreviewCard}>
            <div className={styles.gradePreviewRow}>
              <p className={styles.gradePreviewTitle}>Preview</p>
            {gradeProfilePreviewOptions.length > 0 ? (
              <div className={styles.gradePreviewList}>
                {gradeProfilePreviewOptions.map((option) => (
                  <span
                    key={option.value}
                    className={styles.gradePreviewChip}
                  >
                    <span
                      className={styles.gradePreviewSwatch}
                      style={{ backgroundColor: normalisePreviewColour(option.colour) }}
                    />
                    {option.text}
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.gradePreviewHint}>Select a grade profile to preview its grade options.</p>
            )}
            </div>
          </div>
        </div>

        <div className={`${layout.column1} ${layout.container}`}>           
          <div className={`${layout.rowSpan} ${styles.footerBar}`}>   
            <Link to={getWorkflowPath(templateId || '', templateTitle || '', 'define-fields')}>
                <Button>Back: Define fields</Button>
              </Link>
            <div className={styles.rightButtons}>
              {!isAiPowered && 
              <Tooltip disabled={!(sections.length === 0 || sections[0].criteria.length === 0 || selectedGradeProfile === '')} text="Please add at least one section with one criteria and select a grade profile to proceed." align="top" sideAlign='right'>
                <Button disabled={sections.length === 0 || sections[0].criteria.length === 0 || selectedGradeProfile === ''} clickHandler={() => navigate(getWorkflowPath(templateId || '', templateTitle || '', 'additional-information'))}>{`Next: Additional Information`}</Button>
              </Tooltip>
              }

              {sections.length > 0 && sections[0].criteria.length > 0 && selectedGradeProfile !== '' ? 
              <Tooltip text="AI powered feature. Please upgrade to access. Otherwise click 'Next: Additional Information'." align="top" sideAlign='left' disabled={isAiPowered}>
                {isAiPowered ? 
                  <Button clickHandler={() => navigate(getWorkflowPath(templateId || '', templateTitle || '', 'define-questions'))}>Next: Create Questions <img src='/img/sparkle.svg'/></Button>  
                  :
                  <Button disabled={true}>Next: Create Questions <img src='/img/sparkle.svg'/></Button> 
                }
              </Tooltip>
           : 
              <>
                <Tooltip text="Please add at least one section with one criteria and select a grade profile to proceed." align="top" sideAlign='left'>
                  <Button disabled={true}>Next: Create Questions</Button>
                </Tooltip>
              </>  
            }     
            </div>
          </div>            
        </div>          
      </div>        
    </div>    
    </>  
  );    
};      
  
export default DefineStructure;  